import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { ArrowLeft, LogOut, Search, User, Calendar, Eye } from 'lucide-react';
import { toast } from 'sonner';
import AdminOfferDetailModal from './AdminOfferDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const AdminOffresEnAttente = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [filteredOffers, setFilteredOffers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  useEffect(() => {
    fetchOffers();
  }, []);

  useEffect(() => {
    filterOffers();
  }, [offers, searchTerm]);

  const fetchOffers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Admin voit seulement les offres de sa société avec statut 'en_attente'
        const adminOffers = data.filter(offer => 
          offer.company === user?.company && offer.status === 'en_attente'
        );
        setOffers(adminOffers);
      } else {
        toast.error('Erreur lors du chargement des offres');
      }
    } catch (error) {
      console.error('Error fetching offers:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const filterOffers = () => {
    if (!searchTerm.trim()) {
      setFilteredOffers(offers);
    } else {
      const filtered = offers.filter(offer =>
        offer.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        offer.created_by_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOffers(filtered);
    }
  };

  const handleOfferClick = (offer) => {
    setSelectedOffer(offer);
    setShowDetailModal(true);
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  return (
    <div className="admin-offers-page">
      {/* Header */}
      <header className="admin-header">
        <div className="flex items-center space-x-4">
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            size="sm"
            className="btn-outline"
            style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
          >
            <ArrowLeft size={16} className="mr-2" />
            Retour
          </Button>
          <img
            src={companyLogos[user?.company]}
            alt={`Logo ${user?.company}`}
            className="admin-header-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="admin-header-center">
          <h1>Offres en attente - {user?.company}</h1>
          <p className="admin-header-subtitle">Consultation des offres en attente de validation</p>
        </div>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-offers-container">
          {/* Search Bar */}
          <div className="admin-search-section">
            <div className="admin-search-bar">
              <Search className="admin-search-icon" />
              <input
                type="text"
                placeholder="Rechercher par intitulé ou manager..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="admin-search-input"
              />
            </div>
          </div>

          {/* Offers Header */}
          <div className="admin-offers-header">
            <h2>Offres en attente ({filteredOffers.length})</h2>
          </div>

          {/* Offers List */}
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
            </div>
          ) : filteredOffers.length === 0 ? (
            <div className="admin-empty-state">
              <p>Aucune offre en attente trouvée</p>
            </div>
          ) : (
            <div className="admin-offers-list">
              {filteredOffers.map((offer) => (
                <Card
                  key={offer.id}
                  className="admin-offer-list-item cursor-pointer transition-all duration-200 hover:shadow-lg"
                  onClick={() => handleOfferClick(offer)}
                >
                  <CardContent className="admin-offer-list-content">
                    <div className="admin-offer-list-main">
                      <div className="admin-offer-list-header">
                        <h3 className="admin-offer-list-title">{offer.title}</h3>
                        <div className="admin-offer-badge admin-offer-badge-en-attente">
                          EN ATTENTE
                        </div>
                      </div>
                      
                      <div className="admin-offer-list-info">
                        <div className="admin-offer-info-item">
                          <User className="w-4 h-4 text-gray-500" />
                          <span>Manager: {offer.created_by_name || 'N/A'}</span>
                        </div>
                        
                        <div className="admin-offer-info-item">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span>Créée le {new Date(offer.created_at).toLocaleDateString('fr-FR')}</span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="admin-offer-list-action">
                      <Eye className="w-5 h-5 text-gray-500" />
                      <span className="admin-offer-action-text">Voir détails</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Detail Modal */}
      {showDetailModal && selectedOffer && (
        <AdminOfferDetailModal
          offer={selectedOffer}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedOffer(null);
          }}
        />
      )}
    </div>
  );
};

export default AdminOffresEnAttente;